import type { WatchStopHandle } from "vue"

import { GlobalComposable } from "~/classes/composable/GlobalComposable"
import { PropertyId, PropertySchema } from "~/classes/entity"

class FolderNotes extends GlobalComposable<{
  isEditing: boolean
  /** the (possibly edited, non-synced) property */
  content: string
  fileWatcher?: WatchStopHandle
}> {
  override defaultState = () => ({
    isEditing: false,
    content: "",
  })

  startEditingFolderNotes = () => {
    this.state.value.isEditing = true
  }

  /**
   * stop editing folder notes, optionally taking the file properties otherwise uses
   * current root folder
   */
  stopEditingFolderNotes = (file?: StaticFile) => {
    const { currentRootFolder } = useNavigation()

    if (!this.state.value.isEditing)
      return

    this.state.value.isEditing = false

    const folder = file ?? currentRootFolder.value?.asStatic()

    // handles if notes is empty or if undefined (no notes)
    const notes = folder?.notes()?.trim() ?? ""
    const content = this.state.value.content.trim()
    if (notes === content)
      return this.warn("Notes matches existing, skipping update")
    if (!isDefined(folder))
      throw new Error("Cannot update folder notes when no current folder is set")

    /// delete the property if the content is empty
    folder.updateProperty(
      PropertyId.FILE_NOTES,
      { type: PropertySchema.AnyStringValue, anyString: content },
      content === "",
    )
  }

  stopEditingAllFolderNotes = () => {
    this.stopEditingFolderNotes()
  }

  onCurrentRootFolderChanged = (
    newFile: ReactiveFile | undefined,
    oldFile: ReactiveFile | undefined,
  ) => {
    // if the file ids are different, stop editing the previous file's notes
    if (oldFile && newFile?.id !== oldFile?.id)
      this.stopEditingFolderNotes(oldFile.asStatic())

    const content = newFile?.notes() ?? ""
    if (content === this.state.value.content)
      return

    if (this.state.value.isEditing)
      console.warn("Changing file notes content while it is being edited")

    this.state.value.content = content
  }

  override onCreate = () => {
    const { currentRootFolder } = useNavigation()

    // FIXME: For some reason, updates to the current root folder from inside of
    // onNavigationFileProperties in useNavigation is not getting propagated to this
    // watcher, so the notes property is not getting updated unless we set up a deep watcher.
    // However, this should not generally be necessary and a deep watcher here is a large,
    // deeply nested object so this is very non-performant.
    this.state.value.fileWatcher = watch(() => currentRootFolder.value, this.onCurrentRootFolderChanged)

    const { addInterfaceEventListener } = useEvents()
    addInterfaceEventListener("afterSoftBlur", this.stopEditingAllFolderNotes)
    addInterfaceEventListener("afterHardBlur", this.stopEditingAllFolderNotes)
  }

  override onDestroy = () => {
    const { removeInterfaceEventListener } = useEvents()
    removeInterfaceEventListener("afterSoftBlur", this.stopEditingAllFolderNotes)
    removeInterfaceEventListener("afterHardBlur", this.stopEditingAllFolderNotes)
    this.state.value.fileWatcher?.()
  }
}

const notes = new FolderNotes("folder-notes", theme.colors.violet.hex)
export default function () {
  return notes.use()
}
